import React, { useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import AILightComponent from "../../components/AILightComponent";

const PostHustle = () => {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();
  const userId = authState?.user?._id || null;
  const token = authState?.token;

  const [myPosts, setMyPosts] = useState([]);
  const [showMyPosts, setShowMyPosts] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editMessage, setEditMessage] = useState("");

  const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
  const toggleGenAIHelp = () => setEnableGenAIHelp((p) => !p);

  const [form, setForm] = useState({
    hustleType: "FREELANCE",
    title: "",
    quickJD: "",
    tags: "",
    city: authState.user.city,
    area: authState?.user?.area || "",
    state: authState?.user?.state || "",
    country: authState?.user?.country || "",
    pincode: authState.user.city,
    contactPerson: authState.user.name,
    mobileNumber: authState.user.mobile,
    whatsappNumber: authState.user.mobile,
    emailId: authState.user.email,
    callDays: "",
    callTiming: "",
    externalLink: "",
    dateExpires: "",
  });

  const [message, setMessage] = useState("");

  const loadMyPosts = async () => {
    try {
      if (showMyPosts) {
        setShowMyPosts(false);
        return;
      }
      if (myPosts.length > 0) {
        setShowMyPosts(true);
        return;
      }

      const res = await fetchData({
        url: "/pd/hustles/myposted-list",
        method: "post",
        data: {
          userId: userId,
        },
        headers: { Authorization: token },
      });

      if (res?.status) {
        setMyPosts(res.records || []);
        setShowMyPosts(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = async () => {
    try {
      const requiredFields = [
        "hustleType",
        "title",
        "quickJD",
        "tags",
        "city",
        "area",
        "state",
        "country",
        "pincode",
        "contactPerson",
        "mobileNumber",
        "whatsappNumber",
        "emailId",
        "callDays",
        "callTiming",
        "dateExpires",
      ];

      for (let field of requiredFields) {
        if (!form[field] || form[field].toString().trim() === "") {
          setMessage(`❌ ${field} is required`);
          return;
        }
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const expiry = new Date(form.dateExpires);
      expiry.setHours(0, 0, 0, 0);

      if (expiry < today) {
        setMessage("❌ Expiry date cannot be before today");
        return;
      }

      const payload = {
        ...form,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };

      const res = await fetchData({
        url: "/pd/hustles/create",
        method: "post",
        data: payload,
        headers: { Authorization: token },
      });

      if (res?.status) {
        setMessage("✅ Hustle Posted Successfully");
        setForm({
          hustleType: "FREELANCE",
          title: "",
          quickJD: "",
          tags: "",
          city: authState?.user?.city || "",
          area: authState?.user?.area || "",
          state: authState?.user?.state || "",
          country: authState?.user?.country || "",
          pincode: authState?.user?.pinCode || "",
          contactPerson: authState?.user?.name || "",
          mobileNumber: authState?.user?.mobile || "",
          whatsappNumber: authState?.user?.mobile || "",
          emailId: authState?.user?.email || "",
          callDays: "",
          callTiming: "",
          externalLink: "",
          dateExpires: "",
        });
      } else {
        setMessage("❌ Failed to post hustle");
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ Server Error");
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (num) => /^[6-9]\d{9}$/.test(num.replace(/[^\d]/g, ""));
  const isValidWhatsApp = (num) =>
    /^[0-9]{10,15}$/.test(num.replace(/[^\d]/g, ""));

  const editPost = (item) => {
    setEditingId(item.id);
    setEditMessage("✏️ Editing your Hustles posting");
    setForm({
      hustleType: item.hustleType,
      title: item.title,
      quickJD: item.quickJD,
      tags: Array.isArray(item.tags) ? item.tags.join(", ") : item.tags,
      city: item.city,
      area: item.area,
      state: item.state,
      country: item.country,
      pincode: item.pincode,
      contactPerson: item.contactPerson,
      mobileNumber: item.mobileNumber,
      whatsappNumber: item.whatsappNumber,
      emailId: item.emailId,
      callDays: item.callDays,
      callTiming: item.callTiming,
      externalLink: item.externalLink,
      dateExpires: item.dateExpires?.substring(0, 10),
      isActive: item.isActive ? "Yes" : "No",
    });
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditMessage("");
    setIsSubmitted(false);
  };

  return (
    <div className="space-y-3 pt-3 bg-[#0b0f19] p-3 rounded-xl">
      <div className="text-cyan-300 font-bold text-sm">
        🚀 Post a Hustle Opportunity
      </div>
      <div className="pt-2">
        <button
          onClick={loadMyPosts}
          className="w-fit px-2 py-1 bg-cyan-700 hover:bg-cyan-600 text-white text-xs rounded"
        >
          {showMyPosts ? "Hide My Posting List" : "📋 My Posting List"}
        </button>
      </div>
      {showMyPosts && (
        <div className="mt-2 border-y-2 border-gray-600 rounded-xl py-2">
          <div className="space-y-1 lg:hidden">
            {myPosts.map((item, index) => (
              <div
                key={item.id}
                className="bg-gray-800 rounded-lg px-3 py-1 border border-gray-700"
              >
                <div className="flex justify-between items-center">
                  <div className="text-cyan-300 font-semibold text-xs">
                    #{index + 1} {item.title}
                  </div>
                </div>
                <div className="mt-1 flex justify-between text-[11px] text-gray-400">
                  <div>
                    <span className="text-gray-100">
                      📅 {item.datePosted?.substring(0, 10)}
                    </span>
                    <span
                      className={
                        item.isActive ? "text-green-400" : "text-red-400"
                      }
                    >
                      {item.isActive ? "🟢 Active" : "🔴 Inactive"}
                    </span>
                  </div>
                  <div>
                    <button
                      onClick={() => editPost(item)}
                      className="text-xs lg:text-sm w-fit px-1 py-1 bg-transparent"
                      title="Edit"
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:block overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-cyan-700">
                  <th className="px-2 py-1">SN</th>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Active</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {myPosts.map((item, index) => (
                  <tr key={item.id} className="border-b border-gray-700">
                    <td className="px-2 py-1">{index + 1}</td>
                    <td>{item.datePosted?.substring(0, 10)}</td>
                    <td>{item.title}</td>
                    <td
                      className={
                        item.isActive ? "text-green-400" : "text-red-400"
                      }
                    >
                      {item.isActive ? "Yes" : "No"}
                    </td>
                    <td>
                      <button
                        onClick={() => editPost(item)}
                        className="text-cyan-400 text-base"
                        title="Edit"
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <select
        className="w-1/2 px-2 py-0.5 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
        value={form.hustleType}
        onChange={(e) => setForm({ ...form, hustleType: e.target.value })}
      >
        {[
          "FREELANCE",
          "MICRO_TASK",
          "REMOTE_WORK",
          "WOMEN_JOBS",
          "DIGITAL_BOUTIQUE",
          "DIGITAL_FRANCHISE",
          "STARTUP",
          "FACTORY",
          "CONTRACTING",
          "RETAIL",
        ].map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>

      {/* TITLE */}
      <input
        className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
        placeholder="Title (min 25 characters)"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
      />

      {/* DESCRIPTION */}
      <textarea
        className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
        placeholder="Quick Job Description (min 25 chars, max 500)"
        value={form.quickJD}
        maxLength={500}
        style={{ height: "100px" }}
        onChange={(e) => setForm({ ...form, quickJD: e.target.value })}
      />

      <button
        onClick={toggleGenAIHelp}
        className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold transition-all ${
          enableGenAIHelp
            ? "bg-purple-500 text-white"
            : "bg-[#111827] text-purple-300 border border-purple-500/30"
        }`}
      >
        {enableGenAIHelp ? "🙈 Hide GenAI" : "🤖 Ask GenAI"}
      </button>

      {enableGenAIHelp && (
        <div className="w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
          <AILightComponent
            mode="post_a_hustles"
            param1={"🎯 Smart Hustles Posting Assistant"}
            param2={`Current Form Values:
    Hustle Type: ${form.hustleType}
    Title: ${form.title}
    Quick Job Description: ${form.quickJD}
    Tags: ${form.tags}
    City: ${form.city}
    Pincode: ${form.pincode}
    Contact Person: ${form.contactPerson}
    Mobile Number: ${form.mobileNumber}
    WhatsApp Number: ${form.whatsappNumber}
    Email ID: ${form.emailId}
    Call Days: ${form.callDays}
    Call Timing: ${form.callTiming}
    External Link: ${form.externalLink}
    Expiry Date: ${form.dateExpires}

    Act as a Smart Hustles Posting Assistant.
    Review the above details and:
    If raw then complet all by seeing intent.
    Improve the Title.
    Rewrite the Quick Job Description professionally.
    Suggest better Tags.
    Point out missing or weak information.
    Improve clarity, trust, and conversion.
    Do not invent contact details.
    Return the response in clean sections.
    `}
          />
        </div>
      )}

      {/* TAGS */}
      <input
        className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
        placeholder="Tags (comma separated)"
        value={form.tags}
        onChange={(e) => setForm({ ...form, tags: e.target.value })}
      />

      {/* LOCATION */}
      <div className="grid grid-cols-2 gap-2">
        <input
          className="px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
          placeholder="City"
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
        />

        <input
          className="px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
          placeholder="Pincode"
          value={form.pincode}
          onChange={(e) => setForm({ ...form, pincode: e.target.value })}
        />
      </div>

      {/* CONTACT PERSON */}
      <input
        className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
        placeholder="Contact Person (min 3 chars)"
        value={form.contactPerson}
        onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
      />

      {/* PHONE */}
      <div className="grid grid-cols-2 gap-2">
        <input
          className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
          placeholder="Mobile Number"
          value={form.mobileNumber}
          onChange={(e) => setForm({ ...form, mobileNumber: e.target.value })}
        />

        {/* WHATSAPP */}
        <input
          className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
          placeholder="WhatsApp Number"
          value={form.whatsappNumber}
          onChange={(e) => setForm({ ...form, whatsappNumber: e.target.value })}
        />
      </div>

      {/* EMAIL */}
      <input
        className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
        placeholder="Email ID"
        value={form.emailId}
        onChange={(e) => setForm({ ...form, emailId: e.target.value })}
      />

      {/* CALL DAYS */}
      <input
        className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
        placeholder="Call Days (e.g. Monday,Tuesday OR All Days)"
        value={form.callDays}
        onChange={(e) => setForm({ ...form, callDays: e.target.value })}
      />

      {/* CALL TIMING */}
      <input
        className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
        placeholder="Call Timing (e.g. 10:00-18:00)"
        value={form.callTiming}
        onChange={(e) => setForm({ ...form, callTiming: e.target.value })}
      />

      {/* EXTERNAL LINK */}
      <input
        className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
        placeholder="External Link (optional)"
        value={form.externalLink}
        onChange={(e) => setForm({ ...form, externalLink: e.target.value })}
      />

      {/* EXPIRY */}
      <div className="flex flex-row gap-2">
        <label className="px-2 text-xs lg:text-sm w--fit text-gray-300">
          Expiry Date:{" "}
        </label>
        <input
          type="date"
          className="w-fit px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-300"
          value={form.dateExpires}
          onChange={(e) => setForm({ ...form, dateExpires: e.target.value })}
        />
      </div>

      {/* SUBMIT */}
      <button
        onClick={handleSubmit}
        className="w-fit text-xs lg:text-sm bg-cyan-500 text-black font-bold px-8 py-1 rounded"
      >
        🚀 Submit Hustle
      </button>

      {message && <div className="text-xs text-yellow-300">{message}</div>}
    </div>
  );
};

export default PostHustle;
